/** Общие типы агент-чата (модели + квота) — единый контракт с сервером. */

export type ChatModelId = 'premium' | 'premium_pro';

export type AgentChatModelOption = {
  id: ChatModelId;
  label: string;
  /** DeepSeek-стрим можно прервать. */
  can_stop: boolean;
};

export type AgentChatQuotaSlotPayload = {
  limit: number | null;
  bonus: number;
  used: number;
  remaining: number | null;
  unlimited: boolean;
  /** С сервера: остаток по базовому дневному лимиту. */
  daily_remaining: number | null;
  /** С сервера: остаток по бонусу. */
  bonus_remaining: number | null;
};

export type AgentChatQuotaSnapshot = {
  day_msk: string;
  reset_at: string;
  server_now: string;
  premium: AgentChatQuotaSlotPayload;
  premium_pro: AgentChatQuotaSlotPayload;
};

export function slotRemainingGenerations(
  quota: AgentChatQuotaSnapshot | null,
  slot: ChatModelId
): number | null {
  if (!quota) return null;
  const p = quota[slot];
  if (p.unlimited) return null;
  return p.remaining ?? 0;
}

export function isSlotQuotaBlocked(quota: AgentChatQuotaSnapshot | null, slot: ChatModelId): boolean {
  const r = slotRemainingGenerations(quota, slot);
  return r !== null && r <= 0;
}

export function isAgentChatQuotaSnapshot(x: unknown): x is AgentChatQuotaSnapshot {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  const slotOk = (s: unknown): boolean => {
    if (!s || typeof s !== 'object') return false;
    const sl = s as Record<string, unknown>;
    const u = sl.unlimited;
    const dr = sl.daily_remaining;
    const br = sl.bonus_remaining;
    if (u === true) {
      return (dr === null || typeof dr === 'number') && (br === null || typeof br === 'number');
    }
    return typeof sl.remaining === 'number' && typeof dr === 'number' && typeof br === 'number';
  };
  return (
    typeof o.reset_at === 'string' &&
    typeof o.day_msk === 'string' &&
    slotOk(o.premium) &&
    slotOk(o.premium_pro)
  );
}

/** Ответ `POST /api/chat` при ошибке: тело JSON в `Error.message`. */
export function tryExtractQuotaFromChatTransportError(
  message: string
): AgentChatQuotaSnapshot | null {
  const t = message.trim();
  if (!t.startsWith('{')) return null;
  try {
    const j = JSON.parse(t) as { quota?: unknown };
    if (!j || typeof j !== 'object') return null;
    return isAgentChatQuotaSnapshot(j.quota) ? j.quota : null;
  } catch {
    return null;
  }
}

/** Локальный текст «до сброса» для title (тик снаружи через state). */
export function formatQuotaResetCountdown(resetAtIso: string, nowMs: number): string | null {
  const end = Date.parse(resetAtIso);
  if (Number.isNaN(end)) return null;
  const ms = end - nowMs;
  if (ms <= 0) return 'Лимиты обновятся в 00:00 МСК';
  const totalS = Math.floor(ms / 1000);
  const h = Math.floor(totalS / 3600);
  const m = Math.floor((totalS % 3600) / 60);
  const s = totalS % 60;
  if (h > 0) return `Сброс через ${h}ч ${m}м (МСК)`;
  if (m > 0) return `Сброс через ${m}м ${s}с (МСК)`;
  return `Сброс через ${s}с (МСК)`;
}
